import React, { useState, useEffect } from 'react';
import { ACADEMIA_CONSTANTS } from '../features/academy/AcademiaConstants';
import { Search, SlidersHorizontal, X, CheckCircle2, PlusCircle, Trash2 } from 'lucide-react';

const AdvancedFilterBar = ({ onFilterChange, totalResults }) => {
    // 1. ESTADO INICIAL
    const [filters, setFilters] = useState({ term: '', discipline: 'ALL', bank: 'FGV' });
    const [activeFields, setActiveFields] = useState(['year', 'difficulty', 'normType']); 
    const [isExpanded, setIsExpanded] = useState(false);
    const [showFieldSelector, setShowFieldSelector] = useState(false);

    // 2. CAMPOS DISPONÍVEIS
    const AVAILABLE_FIELDS = [
        { key: 'year', label: 'Ano', type: 'select', options: ACADEMIA_CONSTANTS.YEARS },
        { key: 'difficulty', label: 'Nível', type: 'select', options: Object.entries(ACADEMIA_CONSTANTS.DIFFICULTY).map(([k,v]) => ({ value: k, label: v.label })) },
        { key: 'normType', label: 'Base Legal', type: 'select', options: Object.entries(ACADEMIA_CONSTANTS.NORM_TYPES).map(([k,v]) => ({ value: k, label: v.label })) },
        { key: 'status', label: 'Status da Questão', type: 'select', options: Object.entries(ACADEMIA_CONSTANTS.QUESTION_STATUS).map(([k,v]) => ({ value: k, label: v.label })) },
        { key: 'subject_macro', label: 'Assunto Macro (Tema)', type: 'text' },
        { key: 'subject_micro', label: 'Assunto Micro', type: 'text' },
        { key: 'exam', label: 'Exame Específico', type: 'text' },
        { key: 'article', label: 'Artigo de Lei', type: 'text' },
        { key: 'role', label: 'Cargo Alvo', type: 'select', options: ACADEMIA_CONSTANTS.JOB_ROLES.map(r => ({ value: r, label: r })) }
    ];

    const handleChange = (field, value) => {
        const newFilters = { ...filters, [field]: value };
        setFilters(newFilters);
        onFilterChange(newFilters);
    };

    const toggleField = (fieldKey) => {
        if (activeFields.includes(fieldKey)) {
            setActiveFields(prev => prev.filter(k => k !== fieldKey));
            handleChange(fieldKey, 'ALL');
        } else {
            setActiveFields(prev => [...prev, fieldKey]);
        }
        setShowFieldSelector(false);
    };

    // 3. COMPONENTE WRAPPER UNIFICADO (GARANTIA DE ALINHAMENTO)
    // Força altura fixa no label para que os inputs fiquem alinhados.
    const FilterFieldWrapper = ({ label, onDelete, children }) => (
        <div className="flex flex-col w-full">
            <div className="flex justify-between items-end mb-1.5 min-h-[20px]">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-wider truncate">{label}</label>
                {onDelete && (
                    <button onClick={onDelete} className="text-gray-300 hover:text-red-500 transition-colors">
                        <Trash2 className="w-3 h-3"/>
                    </button>
                )}
            </div>
            {children}
        </div>
    );

    return (
        <div className="bg-white border border-gray-400 rounded-xl shadow-md mb-6 animate-fade-in-up overflow-visible relative z-30">
            
            {/* BARRA SUPERIOR */}
            <div className="p-4 flex flex-col md:flex-row gap-4 justify-between items-center bg-white rounded-xl relative z-20">
                <div className="relative w-full md:w-96 group">
                    <input 
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-400 rounded-lg text-sm focus:bg-white focus:border-[#C5A059] outline-none transition-all placeholder-gray-500 font-bold text-gray-700"
                        placeholder="Pesquisar (Ex: 'Usucapião')..."
                        value={filters.term || ''}
                        onChange={(e) => handleChange('term', e.target.value)}
                    />
                    <Search className="absolute left-3 top-3 text-gray-500 w-4 h-4 group-focus-within:text-[#C5A059]" />
                </div>
                
                <div className="flex gap-3 w-full md:w-auto">
                    <div className="hidden md:flex bg-gray-100 px-4 py-2 rounded-lg text-xs font-bold text-gray-700 border border-gray-300 items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-600"/> 
                        {totalResults}
                    </div>

                    <button onClick={() => setIsExpanded(!isExpanded)} className={`px-4 py-2 rounded-lg text-xs font-bold border transition-all flex items-center gap-2 shadow-sm ${isExpanded ? 'bg-[#20243B] text-white border-[#20243B]' : 'bg-white text-gray-700 border-gray-400 hover:bg-gray-50'}`}>
                        {isExpanded ? <X className="w-3.5 h-3.5" /> : <SlidersHorizontal className="w-3.5 h-3.5" />}
                        FILTROS AVANÇADOS
                    </button>
                </div>
            </div>

            {/* PAINEL EXPANSÍVEL */}
            {isExpanded && (
                <div className="bg-gray-50 border-t border-gray-300 p-6 rounded-b-xl animate-fade-in-down relative z-10">
                    <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-2">
                        <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest">Configuração de Busca</h4>
                        <div className="relative">
                            <button onClick={() => setShowFieldSelector(!showFieldSelector)} className="text-xs font-bold text-blue-600 flex items-center gap-1 hover:underline bg-blue-50 px-2 py-1 rounded">
                                <PlusCircle className="w-3 h-3"/> Adicionar Campo
                            </button>
                            {showFieldSelector && (
                                <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-300 shadow-xl rounded-lg z-50 p-2 grid gap-1">
                                    {AVAILABLE_FIELDS.map(f => (
                                        <button key={f.key} onClick={() => toggleField(f.key)} className={`text-left text-xs px-2 py-1.5 rounded flex items-center justify-between ${activeFields.includes(f.key) ? 'bg-blue-50 text-blue-700 font-bold' : 'hover:bg-gray-50 text-gray-600'}`}>
                                            {f.label} {activeFields.includes(f.key) && <CheckCircle2 className="w-3 h-3"/>}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* GRID DE FILTROS - TODOS USAM FilterFieldWrapper */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                        
                        {/* 1. Disciplina (Fixo) */}
                        <FilterFieldWrapper label="Disciplina">
                            <select className="w-full p-2 border border-gray-400 rounded-lg text-xs bg-white focus:border-[#C5A059] outline-none font-bold text-gray-800 shadow-sm h-[34px]" value={filters.discipline} onChange={(e) => handleChange('discipline', e.target.value)}>
                                <option value="ALL">Todas as Matérias</option>
                                {Object.entries(ACADEMIA_CONSTANTS.SUBJECTS).map(([key, val]) => (<option key={key} value={key}>{val.label}</option>))}
                            </select>
                        </FilterFieldWrapper>

                        {/* 2. Banca (Fixo) */}
                        <FilterFieldWrapper label="Banca">
                            <select className="w-full p-2 border border-gray-400 rounded-lg text-xs bg-white focus:border-[#C5A059] outline-none font-bold text-gray-800 shadow-sm h-[34px]" value={filters.bank} onChange={(e) => handleChange('bank', e.target.value)}>
                                <option value="ALL">Todas</option>
                                {ACADEMIA_CONSTANTS.EXAM_BANKS.map(b => (<option key={b.id} value={b.id}>{b.label}</option>))}
                            </select>
                        </FilterFieldWrapper>

                        {/* 3. Filtros Dinâmicos */}
                        {activeFields.map(fieldKey => {
                            const fieldConfig = AVAILABLE_FIELDS.find(f => f.key === fieldKey);
                            if (!fieldConfig) return null;
                            return (
                                <FilterFieldWrapper key={fieldKey} label={fieldConfig.label} onDelete={() => toggleField(fieldKey)}>
                                    {fieldConfig.type === 'select' ? (
                                        <select className="w-full p-2 border border-gray-400 rounded-lg text-xs bg-white focus:border-[#C5A059] outline-none font-bold text-gray-800 shadow-sm h-[34px]" value={filters[fieldKey] || 'ALL'} onChange={(e) => handleChange(fieldKey, e.target.value)}>
                                            <option value="ALL">Qualquer</option>
                                            {fieldConfig.options.map(opt => {
                                                const val = typeof opt === 'object' ? opt.value : opt;
                                                const lbl = typeof opt === 'object' ? opt.label : opt;
                                                return <option key={val} value={val}>{lbl}</option>;
                                            })}
                                        </select>
                                    ) : (
                                        <input className="w-full p-2 border border-gray-400 rounded-lg text-xs bg-white focus:border-[#C5A059] outline-none font-bold text-gray-800 shadow-sm placeholder-gray-400 h-[34px]" placeholder="Digite..." value={filters[fieldKey] || ''} onChange={(e) => handleChange(fieldKey, e.target.value)} />
                                    )}
                                </FilterFieldWrapper>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdvancedFilterBar;